import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { addCart, delCart } from "../redux/action";
import { useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "../context/AuthContext";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import toast from "react-hot-toast";
import api from "../utils/base_url";
import CategorySidebar from "./CategorySidebar";
import '../styles/products.css';
import CategoryGrid from "./CategoryGrid";
import StockNotifyModal from "./StockNotifyModal";

const getThumbnailUrl = (originalUrl) => {
  if (!originalUrl) return originalUrl;
  const url = originalUrl.replace("/products/", "/thumbnails/");
  const lastDot = url.lastIndexOf(".");
  if (lastDot === -1) return url;
  return url.substring(0, lastDot) + ".webp";
};

const Products = () => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedCat, setSelectedCat] = useState(null);
  const [favorites, setFavorites] = useState([]);

  const [stockError, setStockError] = useState({});
  const [disabledQty, setDisabledQty] = useState({});

  const [notifyModal, setNotifyModal] = useState(false);
  const [notifyProduct, setNotifyProduct] = useState(null);
  const [notifyForm, setNotifyForm] = useState({
    email: "",
    quantity: "",
  });

  const sectionRefs = useRef({});
  // ── NEW: track whether a programmatic scroll is in progress ──
  const isProgrammaticScroll = useRef(false);
  const programmaticScrollTimer = useRef(null);

  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  const state = useSelector((state) => state.handleCart);

  const toggleFavorite = async (productId) => {
    if (!user) {
      toast.error("Please login first!");
      navigate("/login");
      return;
    }

    try {
      const res = await api.post("favorites/toggle/", { product_id: productId, auth_id: user.id });
      const isFav = res.data.favorite;

      setFavorites((prev) =>
        isFav ? [...prev, productId] : prev.filter((id) => id !== productId)
      );

      fetchFavorites();
    } catch (err) {
      console.error(err);
      toast.error("Failed, try again");
    }
  };
  useEffect(() => {
    if (loading || products.length === 0) return;

    const savedY = sessionStorage.getItem("products_scroll_y");

    if (!savedY) return;

    const timer = setTimeout(() => {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          window.scrollTo({
            top: parseInt(savedY, 10),
            behavior: "instant",
          });

          // Clean after successful restore
          sessionStorage.removeItem(
            "products_scroll_y"
          );
        });
      });
    }, 300);

    return () => clearTimeout(timer);
  }, [loading, products]);

  const fetchFavorites = useCallback(async () => {
    if (!user) return;

    try {
      const res = await api.get(`favorites/ids/?auth_id=${user.id}`);
      setFavorites(res.data.favorites || []);
    } catch (err) {
      console.error(err);
    }
  }, [user]);

  const fetchCategories = useCallback(async () => {
    try {
      const res = await api.get("categorylist/");
      setCategories(res.data.data || []);
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  }, []);

  const fetchProducts = useCallback(async (categoryId = null) => {
    setLoading(true);
    try {
      const url = categoryId
        ? `productfilter/${categoryId}/`
        : `productlist/`;

      const res = await api.get(url);
      setProducts(res.data.data || []);
    } catch (error) {
      console.error("Error fetching products:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  const scrollToCategory = useCallback((categoryId) => {
    const selectedCategory = categories.find(cat => cat.id === categoryId);
    const sectionEl = sectionRefs.current[selectedCategory?.category_name];

    if (!sectionEl) {
      toast.error("No products available in this category!");
      return;
    }

    // Immediately highlight the clicked category in the sidebar
    setSelectedCat(categoryId);

    // Block the IntersectionObserver from overriding our choice while scrolling
    isProgrammaticScroll.current = true;
    clearTimeout(programmaticScrollTimer.current);

    const NAVBAR_HEIGHT = 100;

    const doScroll = () => {
      const y =
        sectionEl.getBoundingClientRect().top +
        window.pageYOffset -
        NAVBAR_HEIGHT;

      window.scrollTo({ top: y, behavior: "smooth" });
    };

    doScroll();

    // Allow IntersectionObserver to resume after scroll animation finishes.
    // 1200 ms covers even slow smooth-scroll on low-end Android devices.
    programmaticScrollTimer.current = setTimeout(() => {
      isProgrammaticScroll.current = false;
    }, 1200);
  }, [categories]);

  useEffect(() => {
    fetchCategories();
    fetchFavorites();

    if (!location.state?.categoryId) {
      fetchProducts();
    }
  }, [fetchCategories, fetchProducts, fetchFavorites, location.state]);

  const badgeStyle = {
    position: "absolute",
    top: "8px",
    left: "30%",
    transform: "translateX(-50%)",
    padding: "6px 14px",
    background: "#dc3545",
    color: "white",
    borderRadius: "20px",
    fontSize: "0.8rem",
    fontWeight: 600,
    zIndex: 99,
    whiteSpace: "nowrap",
    boxShadow: "0 3px 10px rgba(0,0,0,0.2)",
  };

  // ── REPLACED rAF scroll listener with IntersectionObserver ─────────────────
  // IntersectionObserver fires correctly even when content-visibility:auto is
  // used, because it observes the element boundary, not its painted rect.
  useEffect(() => {
    if (!categories.length || !products.length) return;

    const NAVBAR_HEIGHT = 100;

    // rootMargin pushes the "active" trigger line down by navbar height so the
    // category activates exactly when its heading crosses the sticky bar.
    const observer = new IntersectionObserver(
      (entries) => {
        // Ignore observer callbacks while a programmatic scroll is running
        if (isProgrammaticScroll.current) return;

        // Find the entry closest to the top of the visible area
        let best = null;
        let bestTop = -Infinity;

        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const top = entry.boundingClientRect.top;
            if (top > bestTop) {
              bestTop = top;
              best = entry;
            }
          }
        });

        if (!best) return;

        const catName = best.target.getAttribute("data-category");
        const catObj = categories.find(c => c.category_name === catName);
        if (catObj) {
          setSelectedCat(catObj.id);
        }
      },
      {
        // The observation viewport starts just below the navbar and extends to
        // the bottom of the screen. Any section whose top enters this band is
        // considered "active".
        rootMargin: `-${NAVBAR_HEIGHT}px 0px -40% 0px`,
        threshold: 0,
      }
    );

    // Observe every rendered section
    Object.values(sectionRefs.current).forEach((el) => {
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, [categories, products]);
  // ── end IntersectionObserver ────────────────────────────────────────────────


  useEffect(() => {
    const categoryId = location.state?.categoryId;

    if (!categoryId || !categories.length) return;

    setSelectedCat(categoryId);
    fetchProducts(categoryId);

    const attemptScroll = () => {
      const selectedCategory = categories.find((cat) => cat.id === categoryId);
      if (selectedCategory && sectionRefs.current[selectedCategory.category_name]) {
        const sectionEl = sectionRefs.current[selectedCategory.category_name];
        const yOffset = -100;
        const y = sectionEl.getBoundingClientRect().top + window.pageYOffset + yOffset;
        window.scrollTo({ top: y, behavior: "smooth" });
      }
    };

    const scrollTimeout = setTimeout(attemptScroll, 1200);

    return () => clearTimeout(scrollTimeout);
  }, [location.state, categories, fetchProducts]);

  const Loading = () => (
    <div className="grid-container">
      {[...Array(6)].map((_, idx) => (
        <div key={idx} className="product-card skeleton-card" style={{ padding: "12px" }}>
          <Skeleton height={140} style={{ borderRadius: "10px" }} />
          <Skeleton height={20} width={120} style={{ marginTop: "12px" }} />
          <Skeleton height={12} width="90%" />
          <Skeleton height={12} width="85%" />
          <Skeleton height={12} width="80%" />
          <Skeleton height={20} width={90} style={{ marginTop: "12px" }} />
          <Skeleton height={35} width="60%" style={{ margin: "10px auto" }} />
        </div>
      ))}
    </div>
  );

  const getCartQty = (productId) => {
    const item = state.find((p) => p.id === productId);
    return item ? item.qty : 0;
  };

  const addItem = (product) => {
    const available = product.available_stock ?? product.stock_quantity;
    const nextQty = getCartQty(product.id) + 1;

    if (nextQty > available) {
      setStockError(prev => ({ ...prev, [product.id]: true }));
      setDisabledQty(prev => ({ ...prev, [product.id]: true }));

      setTimeout(() => {
        setStockError(prev => ({ ...prev, [product.id]: false }));
        setDisabledQty(prev => ({ ...prev, [product.id]: false }));
      }, 2000);

      return;
    }

    dispatch(addCart(product));
  };

  const removeItem = (product) => {
    dispatch(delCart(product));

    setStockError(prev => {
      const newState = { ...prev };
      delete newState[product.id];
      return newState;
    });
  };

  const groupedProducts = useMemo(() => {
    const grouped = products.reduce((acc, product) => {
      const categoryName = product.category_name || "Uncategorized";
      if (!acc[categoryName]) acc[categoryName] = [];
      acc[categoryName].push(product);
      return acc;
    }, {});

    Object.keys(grouped).forEach((catName) => {
      grouped[catName].sort((b, a) => a.id - b.id);
    });
    return grouped;
  }, [products]);

  const sortedCategories = useMemo(() => {
    return [...categories].sort((a, b) => {
      const getOrder = (val) => {
        if (val === null || val === undefined || val === "") return 9999;
        const num = Number(val);
        return isNaN(num) ? 9999 : num;
      };
      const orderA = getOrder(a.order);
      const orderB = getOrder(b.order);
      if (orderA !== orderB) return orderA - orderB;
      return (a.category_name || "").localeCompare(b.category_name || "");
    });
  }, [categories]);

  const RenderProducts = () => {
    return (
      <>
        {sortedCategories
          .filter((cat) => groupedProducts[cat.category_name])
          .map((cat) => {
            const categoryName = cat.category_name;
            const productList = groupedProducts[categoryName];
            return (
              <section
                key={categoryName}
                data-category={categoryName}
                ref={(el) => (sectionRefs.current[categoryName] = el)}
                className="mb-5 product-section"
              >
                <h2
                  style={{
                    color: "#000000ff",
                    display: "inline-block",
                    paddingBottom: "4px",
                    fontSize: "1.1rem",
                    fontWeight: "700",
                    marginBottom: "15px",
                    textTransform: "uppercase",
                  }}
                >
                  {categoryName}
                </h2>

                <div className="grid-container">
                  {productList.map((product, idx) => {
                    const hasOffer =
                      product.offer_percentage &&
                      Number(product.offer_percentage) > 0 &&
                      product.offer_price;

                    const altCardClass = idx % 2 === 0 ? "card-even" : "card-odd";
                    const available = product.available_stock ?? product.stock_quantity;
                    const outOfStock = available === 0;
                    const stockLow = available <= 2 && available > 0;

                    return (
                      <div
                        key={product.id}
                        className={`product-card ${altCardClass}`}
                        style={{ cursor: "pointer" }}
                        onClick={() => {
                          sessionStorage.setItem(
                            "products_scroll_y",
                            window.scrollY
                          );

                          navigate(`/product/${product.id}`);

                        }}
                      >
                        {hasOffer && (
                          <span className="offer-badge">
                            {Math.round(product.offer_percentage)}% OFF
                          </span>
                        )}
                        {outOfStock && (
                          <span className="stock-badge out-stock">Out of Stock</span>
                        )}
                        {!outOfStock && stockLow && (
                          <span className="stock-badge low-stock">
                            Hurry! Only {available} left
                          </span>
                        )}

                        {stockError[product.id] && (
                          <motion.div
                            initial={{ x: 100, opacity: 0 }}
                            animate={{
                              x: [100, 0, -6, 6, -6, 6, 0],
                              opacity: 1,
                            }}
                            transition={{
                              duration: 0.6,
                              ease: "easeOut",
                            }}
                            exit={{
                              x: -100,
                              opacity: 0,
                              transition: { duration: 0.3 }
                            }}
                            style={badgeStyle}
                          >
                            Stock limit reached!
                          </motion.div>
                        )}

                        <div className="img-wrapper">
                          <img
                            src={getThumbnailUrl(product.product_image)}
                            alt={product.product_name}
                            loading="lazy"
                            decoding="async"
                            style={{ backgroundColor: "#fff9f3" }}
                            onError={(e) => { if (e.target.src !== product.product_image) e.target.src = product.product_image; }}
                          />
                        </div>
                        <div className="product-content">
                          <h5 className="product-title">
                            {product.product_name.length > 15
                              ? product.product_name.substring(0, 15) + "..."
                              : product.product_name}
                          </h5>

                          <div
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleFavorite(product.id);
                            }}
                            style={{
                              position: "absolute",
                              top: "10px",
                              right: "10px",
                              cursor: "pointer",
                              zIndex: 50,
                            }}
                          >
                            {user ? (
                              <div
                                style={{
                                  width: "30px",
                                  height: "30px",
                                  borderRadius: "50%",
                                  backgroundColor: "#E9DCC3",
                                  boxShadow: "0 2px 6px #4A1F1A",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  transition: "transform 0.25s ease, box-shadow 0.25s ease",
                                  cursor: "pointer",
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.boxShadow = "0 2px 8px  #4A1F1A";
                                  e.currentTarget.style.transform = "scale(1.08)";
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.boxShadow = "0 2px 6px rgba(0,0,0,0.15)";
                                  e.currentTarget.style.transform = "scale(1)";
                                }}
                              >
                                {favorites.includes(product.id) ? (
                                  <span style={{ color: "#e63946", fontSize: "16px" }}>❤️</span>
                                ) : (
                                  <span style={{ color: "#4A1F1A", fontSize: "16px" }}>🤍</span>
                                )}
                              </div>
                            ) : null}
                          </div>

                          <div className="product-price-wrap">
                            {hasOffer ? (
                              <>
                                <span className="product-price-offer">
                                  ₹ {parseFloat(product.offer_price).toLocaleString()}
                                </span>
                                <span className="product-price-original">
                                  <s>₹ {parseFloat(product.price).toLocaleString()}</s>
                                </span>
                              </>
                            ) : (
                              <span className="product-price">
                                ₹ {parseFloat(product.price).toLocaleString()}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="product-btns">
                          <div className="product-btns" onClick={(e) => e.stopPropagation()}>
                            {product.stock_quantity === 0 ? (
                              <div
                                style={{
                                  display: "flex",
                                  gap: "8px",
                                  alignItems: "center",
                                }}
                              >
                                <button
                                  className="notify-btn"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setNotifyProduct(product);
                                    setNotifyForm({
                                      email: "",
                                      quantity: "",
                                    });
                                    setNotifyModal(true);
                                  }}
                                >
                                  <span style={{ fontSize: "16px" }}>🔔</span>
                                  Notify Me
                                </button>
                              </div>
                            ) : getCartQty(product.id) === 0 ? (
                              <button
                                className="btn-buy"
                                onClick={() => addItem(product)}
                              >
                                Add to Cart
                              </button>
                            ) : (
                              <div className="qty-box">
                                <button className="qty-btn" onClick={() => removeItem(product)}>
                                  <i className="fas fa-minus"></i>
                                </button>
                                <span className="qty-value">{getCartQty(product.id)}</span>
                                <button
                                  className="qty-btn"
                                  onClick={() => addItem(product)}
                                  disabled={disabledQty[product.id]}
                                >
                                  <i className="fas fa-plus"></i>
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </section>
            );
          })}
        <StockNotifyModal
          open={notifyModal}
          onClose={() => setNotifyModal(false)}
          product={notifyProduct}
          notifyForm={notifyForm}
          setNotifyForm={setNotifyForm}
          navigate={navigate}
        />
      </>
    );
  };

  return (
    <>
      <div className="container-fluid px-4 mt-4">
        <CategoryGrid
          categories={categories}
          onCategoryClick={scrollToCategory}
        />
      </div>
      <div className="d-flex align-items-start">
        <div className="d-flex align-items-start products-wrapper">
          <div className="sidebar-follow">
            <CategorySidebar
              categories={categories}
              selectedCat={selectedCat}
              handleCategory={(id) => {
                navigate(`/category/${id}`);
              }}
            />
          </div>

          <div className="flex-grow-1 container my-4 py-3">
            <h2
              id="product-title"
              className="display-6 fw-bold text-center text-uppercase mb-4"
              style={{ color: "#4A1F1A" }}
            >
              Our Products
            </h2>
            {loading ? <Loading /> : RenderProducts()}
          </div>
        </div>
      </div>
    </>
  );
};

export default Products;